/**
 * ETL block config panel (ADR-0038, P4) — the descriptor-driven form for the
 * selected block. Renders one control per `configField` keyed off its
 * `fieldType` (mirror of `hub.etl.descriptors.FieldType`), with conditional
 * visibility (`visibleWhen`). This is the ETL twin of the workflow
 * `NodeEditorDrawer`, but driven by the BLOCK DESCRIPTOR (not a JSON Schema).
 */
import * as React from "react";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Eye, Info, Loader2, Upload, X } from "lucide-react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import {
  CredentialPicker,
  DB_CREDENTIAL_TYPES,
} from "@/components/credentials/CredentialPicker";
import { ExpressionField } from "@/components/workflows/ExpressionField";
import { ItemBatchViewer } from "@/components/workflows/ItemBatchViewer";
import { KeyValueEditor } from "@/components/workflows/KeyValueEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip } from "@/components/ui/tooltip";
import {
  etlApi,
  type BlockConfigField,
  type BlockDescriptor,
  type NodeMetricSnapshot,
  type PreviewSourceResult,
} from "@/lib/api-etl";
import { appsApi } from "@/lib/api-apps";
import { canonicalApi } from "@/lib/api-canonical";
import type { CredentialType } from "@/lib/api-credentials";
import { datasetsApi } from "@/lib/api-datasets";
import { cn } from "@/lib/cn";

import { isFieldVisible } from "./etlGraph";

const SELECT_CLS =
  "h-9 w-full rounded-md border border-border/60 bg-surface px-2 text-sm text-foreground outline-none focus:border-accent";

/** Ambient `.d.ts` for the ETL expression bindings (`js_expr_layer` per-item
 *  eval): `$json`/`$` = the item's data, `$item` the full Item, `$items` the
 *  batch. NO `$('Node')` — cross-node refs are a workflow concept. */
const ETL_EXPR_DTS = `
declare const $json: any;
declare const $: any;
declare const $item: { json: any; binary: Record<string, any>; meta: Record<string, any> };
declare const $items: any[];
declare const $binary: Record<string, any>;
declare const $meta: Record<string, any>;
`;

/** A structured ROW editor's column spec — one entry per `json_array` field
 *  that has a known row shape (E2). Anything not listed keeps the raw JSON
 *  textarea as the fallback. Keyed `<blockType>.<fieldKey>`. */
interface RowCol {
  name: string;
  label: string;
  kind: "text" | "select";
  options?: string[];
  placeholder?: string;
  mono?: boolean;
}

const ROW_SPECS: Record<string, RowCol[]> = {
  "router.routes": [
    { name: "channel", label: "Canale", kind: "text", placeholder: "alto" },
    {
      name: "condition",
      label: "Condizione (JS)",
      kind: "text",
      placeholder: "$json.v > 100",
      mono: true,
    },
  ],
  "cast.casts": [
    { name: "field", label: "Campo", kind: "text", placeholder: "importo" },
    {
      name: "type",
      label: "Tipo",
      kind: "select",
      options: ["string", "integer", "number", "boolean", "date", "datetime", "json"],
    },
  ],
  "validate.expectations": [
    { name: "field", label: "Campo", kind: "text", placeholder: "email" },
    {
      name: "rule",
      label: "Regola",
      kind: "select",
      options: ["not_null", "not_empty", "regex", "min", "max", "enum", "is_email"],
    },
    { name: "value", label: "Valore", kind: "text", placeholder: "(regex/min/max/enum)" },
  ],
  "sort.by": [
    { name: "field", label: "Campo", kind: "text", placeholder: "data" },
    { name: "dir", label: "Direzione", kind: "select", options: ["asc", "desc"] },
  ],
  "aggregate.aggregations": [
    { name: "op", label: "Operazione", kind: "select", options: ["sum", "count", "avg", "min", "max"] },
    { name: "field", label: "Campo", kind: "text", placeholder: "importo" },
    { name: "as", label: "Alias", kind: "text", placeholder: "totale" },
  ],
};

/** `json_array` fields that are a plain list of STRINGS (one input per row). */
const STRING_LIST_KEYS = new Set(["aggregate.group_by"]);

/** Row editor for a `json_array` of shaped objects (router routes, casts,
 *  expectations, sort keys, aggregations). Local buffer seeded once (the panel
 *  is keyed per node) — a freshly-added empty row survives typing; fully-empty
 *  rows are dropped from the EMITTED config, never from the buffer. */
function RowsEditor({
  value,
  onChange,
  columns,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  columns: RowCol[];
  disabled?: boolean;
}) {
  const [rows, setRows] = React.useState<Record<string, string>[]>(() =>
    Array.isArray(value)
      ? (value as Record<string, unknown>[]).map((r) =>
          Object.fromEntries(columns.map((c) => [c.name, String(r?.[c.name] ?? "")])),
        )
      : [],
  );
  const commit = (next: Record<string, string>[]) => {
    setRows(next);
    onChange(next.filter((r) => columns.some((c) => (r[c.name] ?? "").trim() !== "")));
  };
  const setCell = (i: number, name: string, v: string) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, [name]: v } : r)));

  return (
    <div className="space-y-1.5">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-1.5">
          {columns.map((c) =>
            c.kind === "select" ? (
              <select
                key={c.name}
                value={row[c.name] ?? ""}
                onChange={(e) => setCell(i, c.name, e.target.value)}
                disabled={disabled}
                aria-label={c.label}
                className={cn(SELECT_CLS, "h-8 flex-1 text-xs")}
              >
                <option value="">{c.label}</option>
                {(c.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                key={c.name}
                value={row[c.name] ?? ""}
                onChange={(e) => setCell(i, c.name, e.target.value)}
                placeholder={c.placeholder ?? c.label}
                aria-label={c.label}
                disabled={disabled}
                className={cn("h-8 flex-1 text-xs", c.mono && "font-mono")}
              />
            ),
          )}
          <button
            type="button"
            onClick={() => commit(rows.filter((_, idx) => idx !== i))}
            disabled={disabled}
            className="rounded p-1 text-muted-foreground hover:bg-elevated hover:text-danger"
            aria-label="Rimuovi riga"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => commit([...rows, Object.fromEntries(columns.map((c) => [c.name, ""]))])}
      >
        + Aggiungi
      </Button>
    </div>
  );
}

/** Row editor for a `json_array` of plain strings (aggregate group_by). */
function StringListEditor({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [rows, setRows] = React.useState<string[]>(() =>
    Array.isArray(value) ? (value as unknown[]).map((v) => String(v ?? "")) : [],
  );
  const commit = (next: string[]) => {
    setRows(next);
    onChange(next.filter((v) => v.trim() !== ""));
  };
  return (
    <div className="space-y-1.5">
      {rows.map((v, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input
            value={v}
            onChange={(e) => commit(rows.map((r, idx) => (idx === i ? e.target.value : r)))}
            placeholder={placeholder ?? "campo"}
            disabled={disabled}
            className="h-8 flex-1 text-xs"
          />
          <button
            type="button"
            onClick={() => commit(rows.filter((_, idx) => idx !== i))}
            disabled={disabled}
            className="rounded p-1 text-muted-foreground hover:bg-elevated hover:text-danger"
            aria-label="Rimuovi riga"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => commit([...rows, ""])}
      >
        + Aggiungi
      </Button>
    </div>
  );
}

function JsonField({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const [text, setText] = React.useState(() =>
    typeof value === "string" ? value : JSON.stringify(value ?? null, null, 2),
  );
  const [err, setErr] = React.useState<string | null>(null);
  return (
    <div>
      <textarea
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(e.target.value.trim() === "" ? null : JSON.parse(e.target.value));
            setErr(null);
          } catch {
            setErr("JSON non valido");
          }
        }}
        spellCheck={false}
        rows={5}
        className="w-full rounded-md border border-border/60 bg-muted/20 p-2 font-mono text-xs text-foreground outline-none focus:border-accent"
      />
      {err && <p className="mt-1 text-[11px] text-danger">{err}</p>}
    </div>
  );
}

/** Catalog-Dataset picker (`fieldType: "dataset"`) — stores the UUID, shows the
 *  name. A legacy/Odino-written value that isn't a known id (a raw name) is kept
 *  as an extra option so opening the panel never silently blanks the config;
 *  a failed catalog fetch degrades to a plain text input (never a dead field). */
function DatasetPickerField({
  value,
  onChange,
  required,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  required: boolean;
  disabled?: boolean;
}) {
  const q = useQuery({ queryKey: ["datasets"], queryFn: () => datasetsApi.list() });
  const current = (value as string) ?? "";
  if (q.isError) {
    return (
      <Input
        value={current}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ID o nome del dataset"
        disabled={disabled}
      />
    );
  }
  const datasets = q.data ?? [];
  const known = datasets.some((d) => d.id === current);
  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || q.isLoading}
      className="h-9 w-full rounded-md border border-border/60 bg-surface px-2 text-sm text-foreground outline-none focus:border-accent"
    >
      {(!required || !current) && <option value="">—</option>}
      {current && !known && <option value={current}>{current}</option>}
      {datasets.map((d) => (
        <option key={d.id} value={d.id}>
          {d.name}
        </option>
      ))}
    </select>
  );
}

/** Every app's tables, flattened for the two-level `app_table` picker (and the
 *  owning-app lookup the dependent `unique_set` picker needs). Cache-shared with
 *  the app builder via the canonical `["apps", appId, "tables"]` keys. */
function useAppTables() {
  const appsQ = useQuery({ queryKey: ["apps"], queryFn: () => appsApi.list() });
  const apps = appsQ.data ?? [];
  const tableQs = useQueries({
    queries: apps.map((a) => ({
      queryKey: ["apps", a.id, "tables"] as const,
      queryFn: () => appsApi.listTables(a.id),
    })),
  });
  const groups = apps.map((a, i) => ({ app: a, tables: tableQs[i]?.data ?? [] }));
  return { groups, isError: appsQ.isError, isLoading: appsQ.isLoading };
}

/** `fieldType: "app_table"` — app→table picker, stores the table UUID. Same
 *  degradation rules as the Dataset picker: an unknown stored value is kept as
 *  an extra option, a failed fetch falls back to a plain text input. */
function AppTablePickerField({
  value,
  onChange,
  required,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  required: boolean;
  disabled?: boolean;
}) {
  const { groups, isError, isLoading } = useAppTables();
  const current = (value as string) ?? "";
  if (isError) {
    return (
      <Input
        value={current}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ID della tabella"
        disabled={disabled}
      />
    );
  }
  const known = groups.some((g) => g.tables.some((t) => t.id === current));
  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || isLoading}
      className={SELECT_CLS}
    >
      {(!required || !current) && <option value="">—</option>}
      {current && !known && <option value={current}>{current}</option>}
      {groups.map((g) => (
        <optgroup key={g.app.id} label={g.app.name}>
          {g.tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** `fieldType: "unique_set"` — DEPENDENT on the sibling `table_id`: lists the
 *  chosen table's unique sets (the upsert conflict target). No table picked →
 *  a locked hint; an unresolvable table → text fallback. */
function UniqueSetPickerField({
  value,
  onChange,
  tableId,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  tableId: string;
  disabled?: boolean;
}) {
  const { groups } = useAppTables();
  const owner = groups.find((g) => g.tables.some((t) => t.id === tableId));
  const tableQ = useQuery({
    queryKey: ["apps", owner?.app.id, "tables", tableId],
    queryFn: () => appsApi.getTable(owner!.app.id, tableId),
    enabled: Boolean(owner && tableId),
  });
  const current = (value as string) ?? "";
  if (!tableId) {
    return (
      <p className="rounded-md bg-muted/20 p-2 text-[11px] text-muted-foreground">
        Scegli prima la tabella.
      </p>
    );
  }
  if (!owner || tableQ.isError) {
    return (
      <Input
        value={current}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ID dell'unique set"
        disabled={disabled}
      />
    );
  }
  const sets = tableQ.data?.unique_sets ?? [];
  const known = sets.some((s) => s.id === current);
  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || tableQ.isLoading}
      className={SELECT_CLS}
    >
      <option value="">— (l'unico set della tabella)</option>
      {current && !known && <option value={current}>{current}</option>}
      {sets.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

/** `fieldType: "canonical_model"` — the canonical-registry model picker (stores
 *  the model NAME; the sibling version field stays a number). */
function CanonicalModelPickerField({
  value,
  onChange,
  required,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  required: boolean;
  disabled?: boolean;
}) {
  const q = useQuery({
    queryKey: ["canonical", "list"],
    queryFn: () => canonicalApi.list({ page_size: 100 }),
  });
  const current = (value as string) ?? "";
  if (q.isError) {
    return (
      <Input
        value={current}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Nome del modello"
        disabled={disabled}
      />
    );
  }
  const models = Array.from(new Set((q.data?.items ?? []).map((c) => c.canonical_type)));
  const known = models.includes(current);
  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || q.isLoading}
      className={SELECT_CLS}
    >
      {(!required || !current) && <option value="">—</option>}
      {current && !known && <option value={current}>{current}</option>}
      {models.map((m) => (
        <option key={m} value={m}>
          {m}
        </option>
      ))}
    </select>
  );
}

/** `fieldType: "blob"` — upload a file to blob storage and store the returned
 *  `blob_ref`. The current ref stays visible/editable (paste an existing one). */
function BlobUploadField({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = React.useState<string | null>(null);
  const mut = useMutation({
    mutationFn: (file: File) => etlApi.uploadFile(file),
    onSuccess: (res) => {
      setFileName(res.filename);
      onChange(res.blob_ref);
    },
  });
  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <Input
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Riferimento blob"
          disabled={disabled}
          className="font-mono text-xs"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || mut.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {mut.isPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="mr-1 h-3.5 w-3.5" />
          )}
          Carica
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) mut.mutate(f);
          e.target.value = ""; // re-picking the same file must re-fire
        }}
      />
      {fileName && <p className="text-[11px] text-muted-foreground">Caricato: {fileName}</p>}
      {mut.isError && <p className="text-[11px] text-danger">{(mut.error as Error).message}</p>}
    </div>
  );
}

function Field({
  field,
  value,
  onChange,
  error,
  disabled,
  config,
  blockType,
}: {
  field: BlockConfigField;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
  disabled?: boolean;
  /** The whole block config — dependent pickers read sibling values from it. */
  config: Record<string, unknown>;
  /** The block's type — the ROW-editor specs are keyed `<type>.<key>` (E2). */
  blockType: string;
}) {
  const ft = field.fieldType;
  let control: React.ReactNode;

  if (ft === "boolean") {
    control = (
      <Switch checked={Boolean(value)} onCheckedChange={onChange} disabled={disabled} />
    );
  } else if (ft === "number") {
    // Declared bounds (E1) are clamped on commit — the descriptor is UI
    // metadata; the handler's runtime clamp stays the real gate.
    const clamp = (n: number) => {
      let v = n;
      if (field.minValue != null && v < field.minValue) v = field.minValue;
      if (field.maxValue != null && v > field.maxValue) v = field.maxValue;
      return v;
    };
    control = (
      <Input
        type="number"
        value={value === undefined || value === null ? "" : String(value)}
        min={field.minValue}
        max={field.maxValue}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : clamp(Number(e.target.value)))
        }
        placeholder={field.placeholder}
        disabled={disabled}
      />
    );
  } else if (ft === "select") {
    control = (
      <select
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-9 w-full rounded-md border border-border/60 bg-surface px-2 text-sm text-foreground outline-none focus:border-accent"
      >
        {!field.required && <option value="">—</option>}
        {(field.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  } else if (ft === "dataset") {
    control = (
      <DatasetPickerField
        value={value}
        onChange={onChange}
        required={field.required}
        disabled={disabled}
      />
    );
  } else if (ft === "app_table") {
    control = (
      <AppTablePickerField
        value={value}
        onChange={onChange}
        required={field.required}
        disabled={disabled}
      />
    );
  } else if (ft === "unique_set") {
    control = (
      <UniqueSetPickerField
        value={value}
        onChange={onChange}
        tableId={(config.table_id as string) ?? ""}
        disabled={disabled}
      />
    );
  } else if (ft === "canonical_model") {
    control = (
      <CanonicalModelPickerField
        value={value}
        onChange={onChange}
        required={field.required}
        disabled={disabled}
      />
    );
  } else if (ft === "blob") {
    control = <BlobUploadField value={value} onChange={onChange} disabled={disabled} />;
  } else if (ft === "template") {
    // Per-item {{ }} template — the workflow drawer's Fixed|ƒx toggle (E2):
    // the flag IS the presence of `{{`, matching the backend render.
    control = (
      <ExpressionField
        value={(value as string) ?? ""}
        onChange={onChange}
        placeholder={field.placeholder}
        jsAmbientTypes={ETL_EXPR_DTS}
      />
    );
  } else if (ft === "credential") {
    control = (
      <CredentialPicker
        value={(value as string) ?? ""}
        onChange={onChange}
        // A dialect-neutral "db" hint (ETL DB blocks are multi-dialect via dbwrite)
        // expands to every DB credential type; the picker then also folds in
        // `tray_gateway`. "http" expands to the two HTTP auth credentials
        // (http_writer, E3). A concrete type passes through unchanged.
        type={
          field.credentialType === "db"
            ? DB_CREDENTIAL_TYPES
            : field.credentialType === "http"
              ? (["http_bearer", "api_key"] as CredentialType[])
              : (field.credentialType as CredentialType | undefined)
        }
        disabled={disabled}
      />
    );
  } else if (ft === "expression" || ft === "sql") {
    control = (
      <div className="h-28 overflow-hidden rounded-md">
        <CodeEditor
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          language={ft === "expression" ? "javascript" : "plaintext"}
          readOnly={disabled}
          height="100%"
          // ETL binding IntelliSense on bare-JS expression fields (E2) —
          // `inline` drops the gutter so the field reads as an input.
          inline={ft === "expression"}
          jsAmbientTypes={ft === "expression" ? ETL_EXPR_DTS : undefined}
        />
      </div>
    );
  } else if (ft === "key_value" || ft === "object_map") {
    // A dict[str, str] gets the key/value ROW editor (headers/params/mapping) —
    // raw-JSON editing for maps was hostile (E1).
    control = (
      <KeyValueEditor
        value={value as Record<string, string> | null | undefined}
        onChange={onChange}
        keyPlaceholder="chiave"
        valuePlaceholder="valore"
      />
    );
  } else if (ft === "json" || ft === "json_array") {
    // The known row shapes get a structured editor (E2); anything else keeps
    // the raw-JSON textarea as the safety net.
    const specKey = `${blockType}.${field.key}`;
    const rowSpec = ROW_SPECS[specKey];
    if (rowSpec) {
      control = (
        <RowsEditor value={value} onChange={onChange} columns={rowSpec} disabled={disabled} />
      );
    } else if (STRING_LIST_KEYS.has(specKey)) {
      control = (
        <StringListEditor
          value={value}
          onChange={onChange}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      );
    } else {
      control = <JsonField value={value} onChange={onChange} disabled={disabled} />;
    }
  } else if (ft === "textarea") {
    control = (
      <textarea
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
        rows={3}
        className="w-full rounded-md border border-border/60 bg-surface p-2 text-sm text-foreground outline-none focus:border-accent"
      />
    );
  } else {
    // text / connection_string / secret
    control = (
      <Input
        type={ft === "secret" ? "password" : "text"}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="space-y-1">
      <Label className="flex items-center gap-1 text-xs">
        {field.label}
        {field.required && <span className="text-danger">*</span>}
        {field.description && (
          // Tooltip only, never an inline paragraph — the workflow-drawer rule
          // (dense blocks became unreadable with per-field prose).
          <Tooltip content={field.description} side="bottom">
            <button
              type="button"
              tabIndex={-1}
              className="text-muted-foreground hover:text-foreground"
              aria-label={`Info: ${field.label}`}
            >
              <Info className="h-3 w-3" strokeWidth={1.5} />
            </button>
          </Tooltip>
        )}
      </Label>
      {control}
      {error && <p className="text-[11px] text-danger">{error}</p>}
    </div>
  );
}

/** Source-only "Anteprima" — sample the first rows the source would emit. */
function SourcePreview({
  type,
  config,
}: {
  type: string;
  config: Record<string, unknown>;
}) {
  const mut = useMutation({
    mutationFn: () => etlApi.previewSource({ type, config, limit: 20 }),
  });
  const res: PreviewSourceResult | undefined = mut.data;
  const cols = React.useMemo(
    () => (res?.rows.length ? Object.keys(res.rows[0]) : []),
    [res],
  );

  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground">Anteprima sorgente</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
        >
          {mut.isPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Eye className="mr-1 h-3.5 w-3.5" />
          )}
          Anteprima
        </Button>
      </div>
      {mut.isError && (
        <p className="text-[11px] text-danger">{(mut.error as Error).message}</p>
      )}
      {res && (
        <>
          <p className="text-[11px] text-muted-foreground">
            {res.count} riga{res.count === 1 ? "" : "he"}
            {res.truncated && " (primi 20)"}
            {res.dead_lettered > 0 && ` · ${res.dead_lettered} dead-letter`}
          </p>
          {res.rows.length > 0 ? (
            <div className="max-h-48 overflow-auto rounded border border-border/60">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-elevated">
                  <tr>
                    {cols.map((c) => (
                      <th key={c} className="px-2 py-1 text-left font-medium text-muted-foreground">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {res.rows.map((row, i) => (
                    <tr key={i} className="border-t border-border/40">
                      {cols.map((c) => (
                        <td key={c} className="max-w-[10rem] truncate px-2 py-1 font-mono">
                          {typeof row[c] === "object"
                            ? JSON.stringify(row[c])
                            : String(row[c] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">Nessuna riga emessa.</p>
          )}
        </>
      )}
    </div>
  );
}

/** Run-data inspector (ADR-0039 C3) — the first-N output rows the block emitted,
 * with a Dead-letter tab when rejected rows were captured. Reuses the workflow
 * editor's `ItemBatchViewer` (Schema / Table / JSON) over the bounded sample.
 * Source = the live SSE buffer while a run streams, the persisted `node_metrics`
 * on a reopened run — the caller threads either as `metrics`. */
function NodeSampleInspector({ metrics }: { metrics: NodeMetricSnapshot }) {
  const sample = metrics.sample ?? [];
  const deadLetters = metrics.dead_letter_sample ?? [];
  const hasDl = deadLetters.length > 0;
  const [tab, setTab] = React.useState<"out" | "dl">("out");
  const view = tab === "dl" ? deadLetters : sample;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
        <span>
          {metrics.items_in ?? 0} in · {metrics.items_out ?? 0} out
          {(metrics.dead_lettered ?? 0) > 0 && ` · ${metrics.dead_lettered} dead-letter`}
        </span>
        {metrics.duration_ms != null && <span>· {Math.round(metrics.duration_ms)} ms</span>}
      </div>

      {hasDl && (
        <div className="flex gap-1 rounded-md bg-muted/30 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setTab("out")}
            className={cn(
              "flex-1 rounded px-2 py-1",
              tab === "out" ? "bg-surface font-medium text-foreground shadow-sm" : "text-muted-foreground",
            )}
          >
            Output ({sample.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("dl")}
            className={cn(
              "flex flex-1 items-center justify-center gap-1 rounded px-2 py-1",
              tab === "dl" ? "bg-surface font-medium text-danger shadow-sm" : "text-muted-foreground",
            )}
          >
            <AlertTriangle className="h-3 w-3" />
            Scarti ({deadLetters.length})
          </button>
        </div>
      )}

      {view.length > 0 ? (
        <ItemBatchViewer
          payload={view}
          initialTab="table"
          className="rounded-md border border-border/60"
        />
      ) : (
        <p className="rounded-md bg-muted/20 p-3 text-[11px] text-muted-foreground">
          {tab === "dl"
            ? "Nessuno scarto campionato."
            : "Nessun campione: il blocco non ha ancora emesso righe (un blocco buffer-all" +
              " emette solo a fine input), oppure i campioni sono disattivati."}
        </p>
      )}
    </div>
  );
}

export function BlockConfigPanel({
  descriptor,
  label,
  config,
  metrics,
  onChangeLabel,
  onChange,
  onClose,
  readOnly,
}: {
  descriptor: BlockDescriptor;
  label: string;
  config: Record<string, unknown>;
  /** The selected node's run snapshot (live or replayed) — drives the
   * inspector tab (ADR-0039). Absent ⇒ no run data, config-only panel. */
  metrics?: NodeMetricSnapshot;
  onChangeLabel: (v: string) => void;
  onChange: (config: Record<string, unknown>) => void;
  onClose: () => void;
  readOnly?: boolean;
}) {
  const fields = descriptor.configFields.filter((f) => isFieldVisible(f, config));
  const missing = (f: BlockConfigField) =>
    f.required && isFieldVisible(f, config) && (config[f.key] === undefined || config[f.key] === null || config[f.key] === "");

  // The inspector tab appears only when the node carries run data (a sample or a
  // dead-letter sample, or at least counters from a completed run).
  const hasRunData =
    metrics != null &&
    ((metrics.sample?.length ?? 0) > 0 ||
      (metrics.dead_letter_sample?.length ?? 0) > 0 ||
      metrics.items_in != null ||
      metrics.items_out != null);
  const [panelTab, setPanelTab] = React.useState<"config" | "data">("config");
  // Reset to config when a node without run data is selected (the data tab vanished).
  React.useEffect(() => {
    if (!hasRunData) setPanelTab("config");
  }, [hasRunData]);

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-l border-border/60 bg-surface">
      <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-xs uppercase text-muted-foreground">{descriptor.ui.label}</div>
          <div className="truncate text-sm font-medium text-foreground">{label}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:bg-elevated"
          aria-label="Chiudi"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {hasRunData && (
        <div className="flex gap-1 border-b border-border/60 px-3 pt-2 text-xs">
          {(["config", "data"] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setPanelTab(key)}
              className={cn(
                "-mb-px border-b-2 px-2 py-1.5",
                panelTab === key
                  ? "border-accent font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {key === "config" ? "Configurazione" : "Dati"}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
        {panelTab === "data" && metrics ? (
          <NodeSampleInspector metrics={metrics} />
        ) : (
          <>
            <div className="space-y-1">
              <Label className="text-xs">Etichetta</Label>
              <Input value={label} onChange={(e) => onChangeLabel(e.target.value)} disabled={readOnly} />
            </div>
            {descriptor.description && (
              <p className="rounded-md bg-muted/30 p-2 text-[11px] text-muted-foreground">
                {descriptor.description}
              </p>
            )}
            {fields.length === 0 && (
              <p className="text-xs text-muted-foreground">Nessun parametro da configurare.</p>
            )}
            {fields.map((f) => (
              <Field
                key={f.key}
                field={f}
                value={config[f.key]}
                onChange={(v) => onChange({ ...config, [f.key]: v })}
                error={missing(f) ? "Campo obbligatorio" : undefined}
                disabled={readOnly}
                config={config}
                blockType={descriptor.type}
              />
            ))}
            {descriptor.supportsPreview && descriptor.ui.category === "source" && (
              <SourcePreview type={descriptor.type} config={config} />
            )}
          </>
        )}
      </div>

      <div className={cn("border-t border-border/60 px-3 py-2")}>
        <Button variant="outline" className="w-full" onClick={onClose}>
          Chiudi
        </Button>
      </div>
    </div>
  );
}
